// src/features/automacaodeemails/pages/ScreensPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

const ICONS = {
  plus: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  scan: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
  edit: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  trash: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3,6 5,6 21,6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  loader: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ae2-spin">
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
    </svg>
  ),
  x: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
};

export default function ScreensPage() {
  const nav = useNavigate();
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [autoSynced, setAutoSynced] = useState(false);
  const [syncStatus, setSyncStatus] = useState("");
  const [syncError, setSyncError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    url: "",
    descricao: "",
    captureActions: [],
    captureExcludes: "",
  });
  const [saving, setSaving] = useState(false);

  const parseActions = useCallback((value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const parseExcludes = useCallback((value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const loadScreens = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.screens.list();
      setScreens(res?.data || []);
    } catch (err) {
      console.error("Erro ao carregar telas:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScreens();
  }, [loadScreens]);

  useEffect(() => {
    if (loading) return;
    if (screens.length > 0) return;
    if (autoSynced) return;
    setAutoSynced(true);
    handleSync();
  }, [loading, screens.length, autoSynced]);

  async function handleScan(screen) {
    setScanning(screen.id);
    try {
      const res = await api.screens.scan(screen.id);
      alert(`Escaneamento concluido! ${res?.data?.elements_found || 0} elemento(s) detectado(s).`);
      loadScreens();
    } catch (err) {
      alert(`Erro ao escanear: ${err?.message || "Erro desconhecido"}`);
    } finally {
      setScanning(null);
    }
  }

  async function handleSync() {
    setSyncing(true);
    setSyncError("");
    setSyncStatus("");
    try {
      const baseUrl = window.location.origin;
      const res = await api.screens.sync({ baseUrl });
      const data = res?.data || {};
      await loadScreens();
      setSyncStatus(
        `Sincronizacao concluida. Inseridos: ${data.inserted || 0}, Atualizados: ${data.updated || 0}.`
      );
    } catch (err) {
      const code = err?.body?.error || err?.message;
      if (code === "base_url_obrigatoria") {
        try {
          const baseUrl = window.location.origin;
          await api.settings.update("screens_base_url", baseUrl);
          const res = await api.screens.sync({ baseUrl });
          const data = res?.data || {};
          await loadScreens();
          setSyncStatus(
            `Sincronizacao concluida. Inseridos: ${data.inserted || 0}, Atualizados: ${data.updated || 0}.`
          );
          setSyncing(false);
          return;
        } catch (inner) {
          const innerMsg =
            inner?.body?.message ||
            inner?.body?.error ||
            inner?.message ||
            "Erro desconhecido";
          setSyncError(
            `Nao foi possivel definir a URL base automaticamente. ${innerMsg}`
          );
        }
      } else {
        const msg =
          err?.body?.message ||
          err?.body?.error ||
          err?.message ||
          "Erro desconhecido";
        setSyncError(`Erro ao sincronizar: ${msg}`);
      }
    } finally {
      setSyncing(false);
    }
  }

  async function handleDelete(screen) {
    if (!window.confirm(`Tem certeza que deseja excluir "${screen.nome}"?`)) return;
    try {
      await api.screens.delete(screen.id);
      loadScreens();
    } catch (err) {
      alert(`Erro ao excluir: ${err?.message || "Erro desconhecido"}`);
    }
  }

  function openCreateModal() {
    setEditingScreen(null);
    setForm({ nome: "", url: "", descricao: "", captureActions: [], captureExcludes: "" });
    setShowModal(true);
  }

  function openEditModal(screen) {
    setEditingScreen(screen);
    const actions = parseActions(screen.capture_actions);
    const excludes = parseExcludes(screen.capture_excludes);
    setForm({
      nome: screen.nome || "",
      url: screen.url || "",
      descricao: screen.descricao || "",
      captureActions: actions,
      captureExcludes: excludes.join("\n"),
    });
    setShowModal(true);
  }

  async function handleSaveScreen(e) {
    e.preventDefault();
    if (!form.nome || !form.url) {
      alert("Nome e URL sao obrigatorios");
      return;
    }

    const actions = (form.captureActions || [])
      .map((a) => ({
        type: String(a.type || "").toLowerCase(),
        selector: typeof a.selector === "string" ? a.selector.trim() : "",
        wait_ms: Number(a.wait_ms || 0),
      }))
      .filter((a) => {
        if (a.type === "click") return !!a.selector;
        if (a.type === "wait") return a.wait_ms > 0;
        return false;
      });

    const excludes = String(form.captureExcludes || "")
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);

    setSaving(true);
    try {
      if (editingScreen) {
        await api.screens.update(editingScreen.id, {
          nome: form.nome,
          url: form.url,
          descricao: form.descricao,
          capture_actions: actions,
          capture_excludes: excludes,
        });
      } else {
        await api.screens.create({
          nome: form.nome,
          url: form.url,
          descricao: form.descricao,
          capture_actions: actions,
          capture_excludes: excludes,
        });
      }
      setShowModal(false);
      loadScreens();
    } catch (err) {
      alert(`Erro ao salvar: ${err?.message || "Erro desconhecido"}`);
    } finally {
      setSaving(false);
    }
  }

  function addAction() {
    setForm((prev) => ({
      ...prev,
      captureActions: [...(prev.captureActions || []), { type: "click", selector: "", wait_ms: 400 }],
    }));
  }

  function updateAction(index, patch) {
    setForm((prev) => {
      const list = [...(prev.captureActions || [])];
      list[index] = { ...list[index], ...patch };
      return { ...prev, captureActions: list };
    });
  }

  function removeAction(index) {
    setForm((prev) => {
      const list = [...(prev.captureActions || [])];
      list.splice(index, 1);
      return { ...prev, captureActions: list };
    });
  }

  return (
    <div className="ae2-page">
      <div className="ae2-page-header">
        <div>
          <h2>Telas do sistema</h2>
          <p>Cadastre URLs e escaneie automaticamente tabelas, gráficos e cards.</p>
        </div>
        <div className="ae2-actions">
          <button className="ae2-btn ae2-btn-primary" onClick={openCreateModal}>
            {ICONS.plus} Nova tela
          </button>
          <button className="ae2-btn ae2-btn-ghost" onClick={handleSync} disabled={syncing}>
            {syncing ? ICONS.loader : ICONS.scan} Sincronizar
          </button>
        </div>
      </div>

      {syncStatus && <div className="ae2-info success">{syncStatus}</div>}
      {syncError && <div className="ae2-info error">{syncError}</div>}

      {loading ? (
        <div className="ae2-loading">Carregando...</div>
      ) : screens.length === 0 ? (
        <div className="ae2-empty">
          <h3>Nenhuma tela cadastrada</h3>
          <p>Sincronize automaticamente as telas do sistema ou adicione manualmente.</p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="ae2-btn ae2-btn-primary" onClick={handleSync} disabled={syncing}>
              {syncing ? ICONS.loader : ICONS.scan} Sincronizar
            </button>
            <button className="ae2-btn ae2-btn-ghost" onClick={openCreateModal}>
              {ICONS.plus} Adicionar manualmente
            </button>
          </div>
        </div>
      ) : (
        <div className="ae2-grid ae2-cards">
          {screens.map((screen) => (
            <div key={screen.id} className="ae2-card ae2-screen-card">
              <div className="ae2-screen-header">
                <h3>{screen.nome}</h3>
                <span className={`ae2-badge ${screen.ativo ? "success" : "muted"}`}>
                  {screen.ativo ? "Ativa" : "Inativa"}
                </span>
              </div>
              <div className="ae2-screen-url">{screen.url}</div>
              {screen.descricao && <p className="ae2-screen-desc">{screen.descricao}</p>}
              <div className="ae2-screen-meta">
                <span>{screen.elements_count || 0} elemento(s) detectado(s)</span>
                {screen.last_scan_at && (
                  <span>Ultimo scan: {new Date(screen.last_scan_at).toLocaleString("pt-BR")}</span>
                )}
              </div>
              <div className="ae2-screen-actions">
                <button className="ae2-btn ae2-btn-danger" onClick={() => handleDelete(screen)}>
                  {ICONS.trash}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="ae2-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ae2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ae2-modal-header">
              <h2>{editingScreen ? "Editar tela" : "Nova tela"}</h2>
              <button className="ae2-btn ae2-btn-glass" onClick={() => setShowModal(false)}>
                {ICONS.x}
              </button>
            </div>
            <form onSubmit={handleSaveScreen}>
              <div className="ae2-modal-body">
                <div className="ae2-field">
                  <label>Nome *</label>
                  <input
                    type="text"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    placeholder="Ex: Dashboard de Vendas"
                    required
                  />
                </div>
                <div className="ae2-field">
                  <label>URL *</label>
                  <input
                    type="url"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    placeholder="https://sistema.empresa.com/dashboard"
                    required
                  />
                </div>
                <div className="ae2-field">
                  <label>Descricao</label>
                  <textarea
                    value={form.descricao}
                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                    placeholder="Descricao opcional da tela..."
                    rows={3}
                  />
                </div>
                <div className="ae2-field">
                  <label>Subtelas / acoes antes de capturar</label>
                  <div className="ae2-actions-builder">
                    {form.captureActions && form.captureActions.length > 0 ? (
                      form.captureActions.map((action, idx) => (
                        <div key={idx} className="ae2-action-row">
                          <select
                            value={action.type || "click"}
                            onChange={(e) => updateAction(idx, { type: e.target.value })}
                          >
                            <option value="click">Clique em botao/aba</option>
                            <option value="wait">Aguardar</option>
                          </select>
                          {action.type !== "wait" && (
                            <input
                              type="text"
                              value={action.selector || ""}
                              onChange={(e) => updateAction(idx, { selector: e.target.value })}
                              placeholder="Selector CSS (ex: button[data-gerencia='CENTRO'])"
                            />
                          )}
                          <input
                            type="number"
                            min="0"
                            value={action.wait_ms || 0}
                            onChange={(e) => updateAction(idx, { wait_ms: e.target.value })}
                            placeholder="Espera (ms)"
                          />
                          <button
                            type="button"
                            className="ae2-btn ae2-btn-danger"
                            onClick={() => removeAction(idx)}
                          >
                            Remover
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="ae2-muted">Nenhuma acao configurada.</div>
                    )}
                  </div>
                  <button type="button" className="ae2-btn ae2-btn-ghost" onClick={addAction}>
                    {ICONS.plus} Adicionar acao
                  </button>
                  <small className="ae2-muted">
                    Use para abrir abas internas (ex: gerencia) antes do scan/captura.
                  </small>
                </div>
                <div className="ae2-field">
                  <label>Excluir elementos do print (CSS)</label>
                  <textarea
                    value={form.captureExcludes}
                    onChange={(e) => setForm({ ...form, captureExcludes: e.target.value })}
                    placeholder="Um seletor por linha. Ex: .sidebar\n.botao-exportar"
                    rows={3}
                  />
                  <small className="ae2-muted">
                    Esses seletores serao ocultados antes do scan e da captura.
                  </small>
                </div>
              </div>
              <div className="ae2-modal-footer">
                <button type="button" className="ae2-btn ae2-btn-ghost" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="ae2-btn ae2-btn-primary" disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
